import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Grid,
  Chip,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  styled,
  TablePagination,
} from "@mui/material";
import { FilterList, Image, Download, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Componentes estilizados
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    backgroundColor: "#19438C",
    color: "white",
    fontSize: 16,
  },
  "&.MuiTableCell-body": {
    fontSize: 14,
    borderRight: "none",
    borderLeft: "none",
  },
}));

const HistorialMantenimientos = () => {
  const navigate = useNavigate();
  const [historial, setHistorial] = useState([]);
  const [filteredHistorial, setFilteredHistorial] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [talleres, setTalleres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    placa: "",
    tipo: "",
    fecha: "",
    taller: "",
  });

  // Estados para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Cargar vehículos
  useEffect(() => {
    const fetchVehiculos = async () => {
      try {
        const response = await fetch(
          "https://bei360.injupemp.gob.hn:8444/api/vehiculos/"
        );
        if (response.ok) {
          const data = await response.json();
          setVehiculos(data);
        }
      } catch (error) {
        console.error("Error fetching vehículos:", error);
      }
    };

    fetchVehiculos();
  }, []);

  useEffect(() => {
    const fetchHistorial = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://bei360.injupemp.gob.hn:8444/api/historial/"
        );
        if (response.ok) {
          const data = await response.json();

          // Ordenar del más nuevo al más antiguo
          const sortedData = data.sort((a, b) => {
            return b.id - a.id;
          });

          setHistorial(sortedData);
          setFilteredHistorial(sortedData);
        } else {
          console.error("Error fetching historial data");
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistorial();
  }, []);

  // Cargar talleres para mapear nombres
  useEffect(() => {
    const fetchTalleres = async () => {
      try {
        const response = await fetch(
          "https://bei360.injupemp.gob.hn:8444/api/taller/"
        );
        if (response.ok) {
          const data = await response.json();
          setTalleres(data);
        }
      } catch (error) {
        console.error("Error fetching talleres:", error);
      }
    };

    fetchTalleres();
  }, []);

  // Función para obtener información completa del vehículo
  const getInfoVehiculo = (vehiculoId) => {
    if (!vehiculoId) return { placa: "No asignado", marca: "", modelo: "" };

    const vehiculo = vehiculos.find((v) => v.id === vehiculoId);
    return vehiculo
      ? {
          placa: vehiculo.placa || `Vehículo #${vehiculoId}`,
          marca: vehiculo.marca || "",
          modelo: vehiculo.modelo || "",
          año: vehiculo.año || "",
          color: vehiculo.color || "",
        }
      : { placa: `Vehículo #${vehiculoId}`, marca: "", modelo: "" };
  };
  // Función para obtener nombre del taller
  const getNombreTaller = (tallerId) => {
    if (!tallerId) return "No asignado";

    const taller = talleres.find((t) => t.id === tallerId);
    return taller ? taller.nombre : `Taller #${tallerId}`;
  };

  // Función para obtener ciudad del taller (opcional)
  const getCiudadTaller = (tallerId) => {
    if (!tallerId) return "";

    const taller = talleres.find((t) => t.id === tallerId);
    return taller ? taller.ciudad : "";
  };

  // Apply filters
  useEffect(() => {
    let filtered = historial;

    if (filters.placa) {
      filtered = filtered.filter(
        (item) =>
          item.vehiculo_placa &&
          item.vehiculo_placa
            .toLowerCase()
            .includes(filters.placa.toLowerCase())
      );
    }

    if (filters.tipo) {
      filtered = filtered.filter(
        (item) =>
          item.tipo_mantenimiento &&
          item.tipo_mantenimiento.toLowerCase() === filters.tipo.toLowerCase()
      );
    }

    if (filters.fecha) {
      filtered = filtered.filter(
        (item) =>
          item.fecha_realizacion &&
          item.fecha_realizacion.includes(filters.fecha)
      );
    }

    if (filters.taller) {
      filtered = filtered.filter((item) => {
        const nombreTaller = getNombreTaller(item.taller).toLowerCase();
        return nombreTaller.includes(filters.taller.toLowerCase());
      });
    }

    setFilteredHistorial(filtered);
    setPage(0); // Resetear a la primera página cuando cambian los filtros
  }, [filters, historial, talleres]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleViewDetails = (mantenimiento) => {
    navigate(`/Flotas/DetallesMantenimiento/${mantenimiento.id}`);
  };

  const handleDownloadReport = () => {
    // Generar y descargar reporte PDF
    console.log("Descargando reporte...");
    // Aquí iría la lógica para generar el PDF
  };

  const clearFilters = () => {
    setFilters({
      placa: "",
      tipo: "",
      fecha: "",
      taller: "",
    });
  };

  // Handlers para paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calcular datos para la página actual
  const paginatedData = filteredHistorial.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Container>
        <Typography>Cargando historial de mantenimientos...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header y Filtros */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Historial de Mantenimientos
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center" }}
        >
          <FilterList sx={{ mr: 1 }} /> Filtros
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Placa del Vehículo"
              value={filters.placa}
              onChange={(e) => handleFilterChange("placa", e.target.value)}
              placeholder="Ej: HCI9854"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Mantenimiento</InputLabel>
              <Select
                value={filters.tipo}
                label="Tipo de Mantenimiento"
                onChange={(e) => handleFilterChange("tipo", e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="Preventivo">Preventivo</MenuItem>
                <MenuItem value="Correctivo">Correctivo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Taller"
              value={filters.taller}
              onChange={(e) => handleFilterChange("taller", e.target.value)}
              placeholder="Nombre del taller"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Fecha"
              type="date"
              value={filters.fecha}
              onChange={(e) => handleFilterChange("fecha", e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        <Button
          onClick={clearFilters}
          sx={{ mt: 2 }}
          variant="outlined"
          color="error"
        >
          Limpiar Filtros
        </Button>
      </Paper>

      {/* Tabla de resultados */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Vehículo</StyledTableCell>
              <StyledTableCell>Tipo</StyledTableCell>
              <StyledTableCell>Taller</StyledTableCell>
              <StyledTableCell>Fecha Realización</StyledTableCell>
              <StyledTableCell>Kilometraje programado</StyledTableCell>
              <StyledTableCell>Acciones</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item) => {
                const infoVehiculo = getInfoVehiculo(item.vehiculo);
                return (
                  <StyledTableRow key={item.id}>
                    <StyledTableCell>{item.id}</StyledTableCell>
                    <StyledTableCell>
                      <Box>
                        <Chip
                          label={infoVehiculo.placa}
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                        <Typography
                          variant="caption"
                          display="block"
                          color="textSecondary"
                        >
                          {infoVehiculo.marca} {infoVehiculo.modelo}{" "}
                          {infoVehiculo.año ? `- ${infoVehiculo.año}` : ""}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={item.tipo_mantenimiento}
                        color={
                          item.tipo_mantenimiento === "Preventivo"
                            ? "success"
                            : "warning"
                        }
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getNombreTaller(item.taller)}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>{item.fecha_realizacion}</StyledTableCell>
                    <StyledTableCell>
                      {item.kilometraje_programado || "N/A"}
                    </StyledTableCell>
                    <StyledTableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleViewDetails(item)}
                      >
                        <Visibility />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={8} align="center">
                  No se encontraron resultados con los filtros aplicados
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Paginación */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredHistorial.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
        }
      />
    </Container>
  );
};

export default HistorialMantenimientos;
